import React from 'react';

/**
 * Shared category-icon helper used by every card face (canvas card in draggable-card,
 * the deckbuilder cards, and the jambuilder deck preview) so they all render the same
 * icon set the same way.
 *
 * The icons are inline SVGs with a ~164x164 viewBox and NO width/height attributes, so
 * they MUST be given an explicit size — and `flexShrink:0`/`minWidth`, because they are
 * usually placed inside a MUI IconButton whose flex label would otherwise squish the
 * width to a few pixels. Centralising that here is what "properly implements" them
 * everywhere (previously each call site sized them differently, some incorrectly).
 */
import { ReactComponent as Union } from '../element/Icons/union.svg';
import { ReactComponent as Bulb } from '../element/Icons/bulb.svg';
import { ReactComponent as Fire } from '../element/Icons/fire.svg';
import { ReactComponent as Finger } from '../element/Icons/finger.svg';
import { ReactComponent as OrangeUnion } from '../element/Icons/orangeUnion.svg';
import { ReactComponent as Question } from '../element/Icons/question.svg';
import { ReactComponent as Paper } from '../element/Icons/paper.svg';
import { ReactComponent as Smile } from '../element/Icons/smile.svg';
import { ReactComponent as Ok } from '../element/Icons/ok.svg';

type SvgIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

// Category colour -> icon. Matches the mapping previously duplicated across the three cards.
const COLOR_TO_ICON: Record<string, SvgIcon> = {
  '#e398d1': Finger,
  '#e2786c': Fire,
  '#f6a163': Smile,
  '#f4b833': Bulb,
  '#80b7b2': Ok,
  '#68a9e2': Paper,
  '#ac9bfb': Question,
  '#d35fb7': Finger,
  '#da4232': Fire,
  '#e66100': Smile,
  '#e69f00': Bulb,
  '#40b0a6': Ok,
  '#0c7bdc': Paper,
  '#785ef0': Question,
};

interface CategoryIconOptions {
  /** Rendered width/height in px. Default 20. */
  size?: number;
  className?: string;
  /** Extra style, merged last (can override the defaults). */
  style?: React.CSSProperties;
}

/**
 * Pick the icon for a category/colour and render it at a consistent, flex-safe size.
 * - no category        -> Union
 * - category "Blank"    -> OrangeUnion
 * - known colour        -> mapped icon
 * - category but unknown colour -> Union
 */
export const categoryIcon = (
  category?: string | null,
  color?: string | null,
  options: CategoryIconOptions = {},
): React.ReactElement => {
  const { size = 20, className, style } = options;

  let Icon: SvgIcon = Union;
  if (category === 'Blank') {
    Icon = OrangeUnion;
  } else if (category && color && COLOR_TO_ICON[color]) {
    Icon = COLOR_TO_ICON[color];
  }

  const mergedStyle: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    minWidth: `${size}px`,
    flexShrink: 0,
    color: '#fff',
    ...style,
  };

  return <Icon className={className} style={mergedStyle} />;
};

export default categoryIcon;
